import React from 'react'
import styled, { css } from 'styled-components'
import { Tooltip } from 'antd'
import PropTypes from 'prop-types'
import { colours, SPACING, mobileCSS, sizes } from '@qflow/theme'
import { Text, Flex, icons, Spacer } from 'portal/lib/primitives'
import { UpgradeToPro } from './UpgradeToPro'
import { ValueAdd } from './ValueAdd'

const DetailBase = styled.div`
  border: 0px;
  margin: 0;
  padding: 0;
  max-width: none;
`
const DetailEntry = styled(DetailBase)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex: 0 0 auto;

  ${mobileCSS(css`
    flex-direction: column;
    align-items: flex-start;
  `)}

  padding-top: ${SPACING.FINE};
`

const DetailRowChildrenContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 1 0;
  font-size: ${sizes.SMALL_MEDIUM};

  ${mobileCSS(css`
    flex-direction: column;
    width: 100%;
    flex: 1 1 auto;

    margin-top: ${SPACING.FINE};
  `)}
`

const HeaderText = styled(Text)`
  display: flex;
  align-items: center;
  padding-top: 6px;
  gap: 4px;
  color: ${colours.CORE_NAVY};
  font-size: ${sizes.SMALL_MEDIUM};
  font-weight: 500;
  width: 17rem;

  ${mobileCSS(css`
    width: auto;
  `)}
`

const InfractionIcon = styled(Text)`
  width: 1rem;
`
const InfractionDetails = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${mobileCSS(css`
    align-items: start;
    flex-direction: column;
  `)}
`
const SpecialActionContainer = styled.div`
  width: 28px;
  height: 28px;
  margin-right: ${SPACING.FINE};
  margin-left: -3px;
`
const ValueAddContainer = styled.div`
  width: 25px;
  height: 25px;
  margin-right: ${SPACING.FINE};
`

export const DetailRow = ({
  label,
  content,
  infraction,
  children,
  tooltip = null,
  upgradeToPro = false,
  updateToProText = null,
  divider = true,
  testId = null,
  valueAdd = null,
  specialAction = null
}) => {
  const valueAddDetails = { value: content, ...valueAdd }

  const infractionEntry = Array.isArray(infraction)
    ? infraction.length > 0
      ? {
          details: null,
          eventDescriptor: (
            <span>
              {infraction.map((i, idx) => (
                <React.Fragment key={idx}>
                  {i.eventDescriptor}
                  {idx < infraction.length - 1 && <br />}
                </React.Fragment>
              ))}
            </span>
          )
        }
      : null
    : infraction

  return (
    <DetailEntry divider={divider} data-testid={testId}>
      <HeaderText
        style={upgradeToPro ? { color: colours.BATTLESHIP_GREY } : {}}
      >
        <div>{label}</div>
        {tooltip && (
          <Tooltip title={tooltip} placement="right">
            <icons.Info size={sizes.MEDIUM} />
          </Tooltip>
        )}
        {upgradeToPro && <UpgradeToPro rowDetails={updateToProText} />}

        {infractionEntry && !infractionEntry.details && (
          <Tooltip
            title={infractionEntry.eventDescriptor}
            styles={{ body: { width: 'max-content' } }}
            placement="right"
          >
            <InfractionIcon>
              <icons.Flag colour={colours.ERROR} size={sizes.MEDIUM} />
            </InfractionIcon>
          </Tooltip>
        )}
      </HeaderText>

      <DetailRowChildrenContainer>
        <Flex.Row verticalCentre>
          {specialAction && (
            <SpecialActionContainer>{specialAction}</SpecialActionContainer>
          )}
          {!specialAction && (
            <ValueAddContainer>
              {valueAdd && <ValueAdd details={valueAddDetails} />}
            </ValueAddContainer>
          )}
          {content && (
            <Text style={{ fontSize: sizes.SMALL_MEDIUM }}>{content}</Text>
          )}
          {!content && !children && (
            <Text
              style={{
                fontSize: sizes.SMALL_MEDIUM,
                color: colours.BORDER_GREY
              }}
            >
              --
            </Text>
          )}
          {children && (
            <InfractionDetails>
              {children}
              {infractionEntry && infractionEntry.details && (
                <Flex.Row>
                  <Spacer.Fine />

                  <InfractionIcon>
                    <icons.Flag size={sizes.MEDIUM} />
                  </InfractionIcon>

                  <Spacer.Fine />

                  <Text error title={infractionEntry.details}>
                    {infractionEntry.details}
                  </Text>
                </Flex.Row>
              )}
            </InfractionDetails>
          )}
        </Flex.Row>
      </DetailRowChildrenContainer>
    </DetailEntry>
  )
}

DetailRow.propTypes = {
  label: PropTypes.node,
  content: PropTypes.string,
  infraction: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element)
  ]),
  divider: PropTypes.bool
}
